import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const CancelamentoPedidoConcluidoModal = ({ isOpen, onClose, pedido, onCancelSuccess }) => {
  const { hasPermission, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [permissoes, setPermissoes] = useState(null);
  const [checkingPermissions, setCheckingPermissions] = useState(false);

  // Verificar permissões quando o modal abrir
  React.useEffect(() => {
    if (isOpen && pedido) {
      checkPermissions();
    }
  }, [isOpen, pedido]);

  const checkPermissions = async () => {
    setCheckingPermissions(true);
    try {
      // Verificar permissões usando o contexto de autenticação
      const permissions = {
        pode_cancelar_direto: hasPermission('pedidos.cancel_concluded'),
        pode_aprovar: hasPermission('pedidos.approve_cancellation')
      };
      
      setPermissoes(permissions);
    } catch (error) {
      console.error('Erro ao verificar permissões:', error);
    }
    setCheckingPermissions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!motivo.trim() || motivo.trim().length < 10) {
      setError('Motivo deve ter pelo menos 10 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const endpoint = permissoes?.pode_cancelar_direto 
        ? `/cancelamento-concluido/direto/${pedido.idProduto}`
        : `/cancelamento-concluido/solicitar/${pedido.idProduto}`;

      const response = await api.post(endpoint, {
        motivo: motivo.trim()
      });

      if (response.data.success) {
        onCancelSuccess(response.data);
        handleClose();
      } else {
        setError(response.data.message || 'Erro ao processar cancelamento');
      }
    } catch (error) {
      console.error('Erro no cancelamento:', error);
      setError(error.response?.data?.message || 'Erro de conexão com o servidor');
    }

    setLoading(false);
  };

  const handleClose = () => {
    setMotivo('');
    setError('');
    setPermissoes(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Cancelar Pedido Concluído
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {checkingPermissions && (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 mr-2" />
              Verificando permissões...
            </div>
          )}

          {!checkingPermissions && permissoes && (
            <>
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-500 mt-1 mr-2" />
                  <div className="text-sm text-orange-700">
                    <p className="font-semibold mb-1">Atenção!</p>
                    <p>
                      {permissoes.pode_cancelar_direto 
                        ? 'Você tem permissão para cancelar este pedido diretamente. O cancelamento será processado imediatamente.'
                        : 'Você não tem permissão para cancelar diretamente. Será criada uma solicitação para aprovação.'
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Pedido:</strong> #{pedido?.idProduto} - {pedido?.nome}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Cliente:</strong> {pedido?.pessoaNome}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Valor:</strong> R$ {pedido?.valor ? parseFloat(pedido.valor).toFixed(2) : '0,00'}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Status:</strong> {pedido?.status}
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Motivo do Cancelamento *
                  </label>
                  <textarea
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Descreva o motivo do cancelamento (mínimo 10 caracteres)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={4}
                    required
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {motivo.length}/10 caracteres mínimos
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded">
                    {error}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                    disabled={loading || motivo.trim().length < 10}
                  >
                    {loading && <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />}
                    {permissoes.pode_cancelar_direto ? 'Cancelar Pedido' : 'Solicitar Cancelamento'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelamentoPedidoConcluidoModal;
